import clsx from 'clsx';
import React, { useCallback, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import ClickAwayListener from '@/src/components/common/ClickAwayListener/ClickAwayListener';
import Icon, { ICONS } from '@/src/components/common/Elements/Icon/Icon';
import LongTextByWidth from '@/src/components/common/Elements/LongText/LongTextByWidth';
import { useToggle } from '@/src/hooks/useToggle';

import ActionButton from '../../../../Elements/ActionButton/ActionButton';
import { useTreatments } from '../../../../store/treatments.store';
import { DropdownOption } from '../../../../types';
import { AutomatedDraft } from '../../../../types/draft';
import FormGroupContainer from '../../Form/FormGroupContainer/FormGroupContainer';
import useDiscardSupplier from '../../Form/SupplierForm/useDiscardSupplier';
import FieldWrapper from '../FieldWrapper/FieldWrapper';
import SelectField, { renderWithTypeOptions } from '../SelectField/SelectField';

type Option = {
  label: string;
  value: string;
  onDiscard: (e: React.MouseEvent) => void;
};

const DraftGeneratedSupplier = ({ label, onDiscard }: Option) => {
  return (
    <React.Fragment>
      <div
        className={clsx(
          'tw-flex tw-items-center',
          'tw-w-auto  tw-min-w-fit',
          'tw-bg-[rgba(240,232,250,1)]',
          'tw-px-[10px] tw-py-[2px]'
        )}
      >
        <LongTextByWidth
          className={clsx('tw-text-cfo-black')}
          id={'draftSupplier__id'}
          value={label}
          maxWidth={280}
          maxLines={1}
        />

        <ActionButton
          className={clsx('tw-group', 'tw-text-cfo-red-400', '!tw-p-0 tw-m-0', 'tw-w-auto')}
          buttonType="style_unset"
          onClick={onDiscard}
        >
          <Icon
            icon={ICONS.close}
            className={clsx(
              '!tw-text-[12px] !tw-text-cfo-red-400',
              '!tw-bg-white active:!tw-bg-white',
              'tw-p-[4px] tw-ml-[10px] tw-my-[2px]',
              'tw-rounded-[25px]',
              'group-hover:!tw-bg-cfo-red-400 group-hover:!tw-text-white'
            )}
          />
        </ActionButton>
      </div>
    </React.Fragment>
  );
};

const SupplierField = ({ control, dropdowns, onCreate }) => {
  const { draft } = useTreatments();
  const [isOptionsOpen, toggleIsOptionsOpen] = useToggle(false);
  const { DiscardDraftSupplierModal, confirmDraftSupplierDiscard } = useDiscardSupplier();
  const { setValue: setParentFormValue, getValues: getParentFormValues } = useFormContext(); // retrieve all hook methods
  const detectedVendor = getParentFormValues('detectedVendor');
  const currentPayeeVendor =
    draft && draft.type === 'Bill'
      ? (draft as AutomatedDraft)?.payload?.vendor
      : (draft as AutomatedDraft)?.payload?.payee;

  const isAutomatedField =
    draft && draft.type === 'Bill'
      ? (draft as AutomatedDraft)?.payload?.vendorIsAutomated
      : (draft as AutomatedDraft)?.payload?.payeeIsAutomated;

  const draftVendor = useMemo(() => {
    if (!draft) {
      return;
    }

    if (!detectedVendor || !detectedVendor.displayName) {
      return null;
    }

    if (currentPayeeVendor) {
      return null;
    }

    const vendorInOption = dropdowns.payees.find((vendor) => vendor.label === vendor.displayName);

    return (
      vendorInOption || {
        label: detectedVendor.displayName,
        value: detectedVendor.displayName,
      }
    );
  }, [draft, detectedVendor, currentPayeeVendor, dropdowns.payees]);

  const handleDiscardDetectedVendor = useCallback(async (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }

    if (!(await confirmDraftSupplierDiscard())) {
      return false;
    }

    setParentFormValue('detectedVendor', null);
    return true;
  }, []);

  const handleRenderSelected = (props: Option) => {
    return (
      <DraftGeneratedSupplier
        {...props}
        label={draftVendor?.label}
        value={draftVendor?.value}
        onDiscard={handleDiscardDetectedVendor}
      />
    );
  };

  const handleSelectOption = useCallback(
    async (selectedOption: DropdownOption) => {
      if (!draft || !detectedVendor) {
        return true;
      }

      if (
        selectedOption.label !== currentPayeeVendor?.name &&
        !(await handleDiscardDetectedVendor())
      ) {
        return false;
      }
      return true;
    },
    [draft, currentPayeeVendor, detectedVendor, handleDiscardDetectedVendor]
  );

  const FirstOption = useCallback(() => {
    return (
      <div className={clsx('tw-flex tw-items-center')}>
        <ActionButton
          className={clsx(
            'tw-text-cfo-blue-600',
            '!tw-p-0 tw-my-0 tw-ml-0 tw-mr-[5px]',
            'tw-w-auto',
            '!tw-justify-start'
          )}
          buttonType="style_unset"
        >
          <Icon icon={ICONS.add} className={clsx('tw-text-[16px] tw-mr-[3px]')} />
          Create
        </ActionButton>
        {draftVendor && (
          <DraftGeneratedSupplier
            label={draftVendor.label}
            value={draftVendor.value}
            onDiscard={handleDiscardDetectedVendor}
          />
        )}
      </div>
    );
  }, [draftVendor, handleDiscardDetectedVendor]);

  const firstOption = {
    isSelectable: true,
    Component: FirstOption,
    onChange: onCreate,
  };

  const renderSelected = isOptionsOpen ? () => null : handleRenderSelected;

  return (
    <FormGroupContainer className={clsx('tw-w-full tw-mt-[15px]')}>
      <ClickAwayListener onClickAway={() => toggleIsOptionsOpen(false)}>
        <FieldWrapper
          label={'Supplier/Payee'}
          required={true}
          labelWidthClasses={clsx('tw-min-w-[98px] tw-max-w-[98px]')}
          labelClassName={clsx('tw-text-cfo-grey-600')}
        >
          <Controller
            name={`payee`}
            control={control}
            rules={{ required: true }}
            render={({ field }) => {
              const selected = field.value || draftVendor || null;
              const isDraftValueUsed = draftVendor && selected.value === draftVendor?.label;
              const isDetectedVendorUsed =
                (detectedVendor &&
                  draft.payload.vendor &&
                  selected?.value === draft.payload.vendor.id) ||
                (isAutomatedField &&
                  !!currentPayeeVendor &&
                  currentPayeeVendor.id === selected?.value);
              const draftVendorIsNotExisting = isDraftValueUsed && !draftVendor.publishData;

              return (
                <SelectField
                  isSearchable={true}
                  renderOption={renderWithTypeOptions}
                  selected={selected}
                  errorSettings={{
                    hasError: draftVendorIsNotExisting,
                    content: draftVendorIsNotExisting && 'Supplier not yet existing.',
                  }}
                  options={dropdowns.payees}
                  onChange={field.onChange}
                  renderSelected={isDraftValueUsed ? renderSelected : null}
                  firstOption={firstOption}
                  classes={{
                    container: 'tw-w-full',
                  }}
                  onSelectOption={handleSelectOption}
                  onClick={toggleIsOptionsOpen}
                  isBackspaceClearEnabled={!isDraftValueUsed}
                  isAiGenerated={isDetectedVendorUsed}
                  showActiveIcon={false}
                />
              );
            }}
          />
          <DiscardDraftSupplierModal />
        </FieldWrapper>
      </ClickAwayListener>
    </FormGroupContainer>
  );
};

export default SupplierField;
